import { FC, useState, useEffect, useCallback } from 'react'
import { getUsersByType, getQueueByPluginspace } from '../services'
import Swal from 'sweetalert2'
import { useQueueActions } from '../hooks/useQueueActions'

interface AvailableSellersTabProps {
  pluginspaceId: number
  onSellerAdded: () => void
}

export const AvailableSellersTab: FC<AvailableSellersTabProps> = ({
  pluginspaceId,
  onSellerAdded,
}) => {
  const [sellers, setSellers] = useState<any[]>([])
  const [queueSellerIds, setQueueSellerIds] = useState<Set<number>>(new Set())
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const { addToQueue, loading: actionLoading } = useQueueActions(pluginspaceId)

  const loadSellers = useCallback(async () => {
    try {
      setLoading(true)
      const [data, queue] = await Promise.all([
        getUsersByType('seller'),
        getQueueByPluginspace(pluginspaceId),
      ])

      const sellerIdsInQueue = new Set(
        (queue || []).map((entry) => Number(entry.sellerId)).filter((id) => Number.isFinite(id))
      )

      setSellers(data)
      setQueueSellerIds(sellerIdsInQueue)
    } catch (error) {
      Swal.fire('Erro', 'Erro ao carregar vendedores', 'error')
    } finally {
      setLoading(false)
    }
  }, [pluginspaceId])

  useEffect(() => {
    loadSellers()
  }, [loadSellers])

  const handleAddToQueue = async (sellerId: number, sellerName: string) => {
    try {
      const result = await Swal.fire({
        title: 'Adicionar à fila?',
        text: `${sellerName} será adicionado à fila`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim, adicionar',
        cancelButtonText: 'Cancelar',
      })

      if (result.isConfirmed) {
        const success = await addToQueue(sellerId)
        if (success) {
          Swal.fire('Sucesso!', `${sellerName} adicionado à fila`, 'success')
          onSellerAdded()
          loadSellers()
        } else {
          Swal.fire('Erro', 'Erro ao adicionar à fila', 'error')
        }
      }
    } catch (error) {
      Swal.fire('Erro', 'Erro ao processar solicitação', 'error')
    }
  }

  const filteredSellers = sellers.filter(
    (seller) =>
      seller.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      seller.email.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (loading) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{ minHeight: '300px' }}>
        <div className='spinner-border' role='status'>
          <span className='sr-only'>Carregando...</span>
        </div>
      </div>
    )
  }

  return (
    <div className='card'>
      <div className='card-header'>
        <h3 className='card-title'>Vendedores Disponíveis</h3>
        <div className='card-toolbar'>
          <input
            type='text'
            className='form-control'
            placeholder='Buscar vendedor...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ maxWidth: '300px' }}
          />
        </div>
      </div>
      <div className='card-body'>
        <div className='table-responsive'>
          <table className='table table-row-dashed table-row-gray-300 gy-7'>
            <thead>
              <tr className='fw-bold fs-6 text-gray-800'>
                <th>Nome</th>
                <th>Email</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredSellers.length === 0 ? (
                <tr>
                  <td colSpan={4} className='text-center py-5'>
                    Nenhum vendedor encontrado
                  </td>
                </tr>
              ) : (
                filteredSellers.map((seller) => (
                  <tr key={seller.id}>
                    {(() => {
                      const alreadyInQueue = queueSellerIds.has(Number(seller.id))

                      return (
                        <>
                    <td>
                      <span className='fw-bold'>{seller.name}</span>
                    </td>
                    <td>{seller.email}</td>
                    <td>
                      <span className={`badge ${alreadyInQueue ? 'badge-light-warning' : 'badge-light-success'}`}>
                        {alreadyInQueue ? 'Já em Fila' : 'Disponível'}
                      </span>
                    </td>
                    <td>
                      {!alreadyInQueue && (
                        <button
                          className='btn btn-sm btn-primary'
                          onClick={() => handleAddToQueue(seller.id, seller.name)}
                          disabled={actionLoading}
                        >
                          <i className='fas fa-plus'></i> Adicionar à Fila
                        </button>
                      )}
                    </td>
                        </>
                      )
                    })()}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
